import { useMemo, useState } from 'react';
import {
  Box,
  useColorModeValue,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  AlertDescription,
  Heading,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import {
  MvrTypeSelector,
  MvrRequestForm,
  MvrRequestStatus,
  MvrRequestReportList,
} from './components';
import {
  useCarriers,
  useMvrRequest,
  useMvrRequestStatus,
  useMvrEnabledProvinces,
  useMvrRequestList,
} from 'hooks/index';
import { useUrlFilters } from 'hooks/useUrlFilters';
import { useViewAs } from 'hooks/useViewAs';
import type {
  MVRRequestTransactionType,
  MVRRequestPayload,
  MVRReportListFilters,
} from 'api';

const MVR_ORDER_FILTER_KEYS = [
  'driver',
  'carrier',
  'requested_by',
  'transaction_type',
  'date_from',
  'date_to',
  'sort_field',
  'sort_direction',
] as const;

const MVR_ORDER_DEFAULTS: Record<string, string> = {
  sort_field: 'created_at',
  sort_direction: 'desc',
};

const MvrRequestView = () => {
  const bgColor = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const { carriers, isLoadingCarriers, carriersError } = useCarriers();
  const { provinceOptions, isLoadingProvinces } = useMvrEnabledProvinces();
  const { submitRequest, isSubmitting } = useMvrRequest();

  const viewAs = useViewAs();
  const {
    getFilter,
    setFilter,
    setFilters,
    setFiltersAndPage,
    page,
    setPage,
    resetAll,
  } = useUrlFilters(MVR_ORDER_FILTER_KEYS, MVR_ORDER_DEFAULTS);

  const reportFilters: MVRReportListFilters = useMemo(
    () => ({
      driver: getFilter('driver'),
      carrier: getFilter('carrier'),
      requested_by: getFilter('requested_by'),
      transaction_type: getFilter('transaction_type'),
      date_from: getFilter('date_from'),
      date_to: getFilter('date_to'),
      sort_field: getFilter('sort_field'),
      sort_direction: getFilter('sort_direction'),
      ...viewAs.filterParams,
    }),
    [getFilter, viewAs.filterParams],
  );

  const {
    reports,
    reportsCount,
    isLoadingReports,
    isRefreshingReports,
    reportsError,
    refetchReports,
  } = useMvrRequestList(page, reportFilters);

  const [selectedType, setSelectedType] =
    useState<MVRRequestTransactionType | null>(null);
  const [activeOrderId, setActiveOrderId] = useState<number | null>(null);
  const { status: orderStatus, isLoading: isLoadingStatus } =
    useMvrRequestStatus(activeOrderId);

  const handleSelectType = (type: MVRRequestTransactionType) => {
    setSelectedType(type);
  };

  const handleFormSubmit = async (payload: MVRRequestPayload) => {
    const result = await submitRequest(payload);
    if (result.success && result.mvrOrderId) {
      setActiveOrderId(result.mvrOrderId);
      setSelectedType(null);
    }
    return result;
  };

  const handleReset = () => {
    setActiveOrderId(null);
    setSelectedType(null);
    refetchReports();
  };

  if (carriersError) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Card bg={bgColor} p="20px">
          <Alert status="error">
            <AlertIcon />
            <Box>
              <AlertDescription>
                Failed to load carriers. Please refresh the page and try again.
              </AlertDescription>
            </Box>
          </Alert>
        </Card>
      </Box>
    );
  }

  return (
    <Card
      bg={bgColor}
      boxShadow="0px 3px 10px rgba(112, 144, 176, 0.12)"
      borderRadius="15px"
      mb="20px"
      p="0"
    >
      <Box borderBottom="1px solid" borderColor={borderColor} p="20px">
        <Heading size="lg" color={textColor}>
          MVR Request
        </Heading>
      </Box>

      <Box p="20px">
        {activeOrderId ? (
          <MvrRequestStatus
            mvrOrderId={activeOrderId}
            status={orderStatus}
            isLoading={isLoadingStatus}
            onReset={handleReset}
          />
        ) : isLoadingCarriers && carriers.length === 0 ? (
          <Center py={10}>
            <Spinner size="xl" color="brand.500" />
          </Center>
        ) : (
          <>
            <Box mb={6}>
              <Text fontSize="md" fontWeight="700" color={textColor} mb={3}>
                Select a transaction type
              </Text>
              <MvrTypeSelector
                selectedType={selectedType}
                onSelect={handleSelectType}
              />
            </Box>

            <Box mb={6}>
              {selectedType ? (
                <MvrRequestForm
                  key={selectedType}
                  requestType={selectedType}
                  carriers={carriers}
                  provinceOptions={provinceOptions}
                  isLoadingProvinces={isLoadingProvinces}
                  isSubmitting={isSubmitting}
                  onSubmit={handleFormSubmit}
                />
              ) : (
                <Text fontSize="sm" color="gray.500">
                  Select a transaction type above to start an MVR request.
                </Text>
              )}
            </Box>

            <Box mb={6}>
              <Text fontSize="md" fontWeight="700" color={textColor} mb={3}>
                Recent Requests
              </Text>
              <MvrRequestReportList
                reports={reports}
                count={reportsCount}
                isLoading={isLoadingReports}
                isRefreshing={isRefreshingReports}
                error={reportsError}
                onRefresh={refetchReports}
                page={page}
                onSetPage={setPage}
                getFilter={getFilter}
                setFilter={setFilter}
                setFilters={setFilters}
                setFiltersAndPage={setFiltersAndPage}
                onResetFilters={resetAll}
              />
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

export default MvrRequestView;
